import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buroApi, fusionarFilas } from './api';
import { BuroCeldaEditor, BuroRecuperacionEditor, distintivoRecuperacion } from './BuroCeldaEditor';
import { formatoDe } from './formatos';
import { COL_CLAVE_OBSERVACION, explicacionBuro } from './explicacionBuro';
import { BuroListaEditor } from './BuroListaEditor';
import { BuroFichaEmpresa } from './BuroFichaEmpresa';
import { BuroFichaCliente } from './BuroFichaCliente';
import { BuroConfigPm } from './BuroConfigPm';
import { BuroHistorial } from './BuroHistorial';
import { AjustesModal, Cargando, CerrarModal, CsvProtegidoModal, ExcluirModal, RegenerarModal } from './BuroModales';
import { BuroPortal, ChipEstatus, entero, fechaCorte, mensajeError, mesLargo, money0, money2 } from './helpers';
import {
  erroresDe, esAviso, esNumerica, estadoCelda, estadoFila, norm, valorDe, AYUDA,
} from './columnas';
import { useAdminToast } from '../shell/AdminToastContext';
import './buro-reportes.css';

// [fondo, texto, ícono] por estado de celda (§4 del diseño).
const COLOR = {
  sys: ['#FFFFFF', '#0C2036', ''],
  sug: ['#FFF6E5', '#8A6D2A', 'pi-info-circle'],
  err: ['#FDECEC', '#B91C1C', 'pi-exclamation-triangle'],
  man: ['#E8F7FD', '#0E5E80', 'pi-pencil'],
  fix: ['#F8FAFC', '#94A3B8', ''],
  exc: ['#F8FAFC', '#94A3B8', ''],
};
// [ícono, color, título] por estado de fila.
const FILA = {
  ok: ['pi-check', '#23B56E', 'Lista'],
  err: ['pi-exclamation-triangle', '#B91C1C', 'Tiene datos faltantes o con error'],
  sug: ['pi-info-circle', '#8A6D2A', 'Tiene sugerencias por confirmar'],
  excl: ['pi-ban', '#94A3B8', 'Excluida'],
};

// Columnas cuyo error cuenta como "datos del cliente faltantes": las corregibles
// en el sistema (el backend las manda; 0 = tipo de persona). En PM solo las que
// edita la ficha de empresa (tipo y segmento EM): las listas AC/AV se corrigen en su editor.
const columnasDeFicha = (reporte, pm) => new Set((reporte.columnas_corregibles || []).map(Number)
  .filter((c) => !pm || c === 0 || (c >= 1000 && c < 2000)));

// Los avales no entran.
const clientesFaltantes = (reporte, pm) => {
  const cols = columnasDeFicha(reporte, pm);
  const vistos = [];
  reporte.filas.forEach((f) => {
    if (f.incluir && !f.aval && f.errores.some((e) => cols.has(Number(e.columna))) && !vistos.includes(f.clienteKey)) {
      vistos.push(f.clienteKey);
    }
  });
  // Los contratos sin cliente ligado se listan al final: solo se corrigen en el reporte.
  const sinCliente = (k) => reporte.filas.some((f) => f.clienteKey === k && !f.cliente_ligado);
  return [...vistos.filter((k) => !sinCliente(k)), ...vistos.filter(sinCliente)];
};

// Columna donde vive cada aviso de "confirmar con Buró" (VR/FR), para navegar a ella.
const AVISO_COL = { pendiente_buro_vr: '3018', pendiente_buro_fr: '3022' };

// Celdas pendientes por formato; los rangos AC/AV se abren en el editor de lista.
const celdasPendientes = (filas, formato) => {
  const out = [];
  const vistos = new Set();
  Object.entries(formato.tabs).forEach(([tab, columnas]) => columnas.forEach((c) => {
    filas.forEach((f) => {
      const key = `${f.id}-${c.k}`;
      if (f.incluir && estadoCelda(f, c) === 'err' && !vistos.has(key)) {
        out.push({ filaId: f.id, k: c.k, tab }); vistos.add(key);
      }
    });
  }));
  formato.todas.forEach((c) => filas.forEach((f) => {
    if (f.incluir && estadoCelda(f, c) === 'err' && !vistos.has(`${f.id}-${c.k}`)) out.push({ filaId: f.id, k: c.k, tab: 'todas' });
  }));
  return out;
};

// Las exclusiones que hace el sistema llevan una marca interna: no se muestra.
const MARCA_AUTO = /^\[auto-buro\]\s*/;
const textoExclusion = (f) => `${MARCA_AUTO.test(f.motivo_exclusion || '') ? 'Excluida por el sistema' : 'Excluida'}: ${String(f.motivo_exclusion || '').replace(MARCA_AUTO, '')}`;

const textoCelda = (fila, c) => {
  if ((c.lista === 'AC' || c.segmento === 'AC') && fila.tipo_persona === 'PFAE') return 'No aplica para PFAE';
  const raw = c.fija ? '' : valorDe(fila, c);
  if (c.fija) return '—';
  if (raw === '' || raw == null) return '';
  return c.kind === 'amount' ? entero(raw) : raw;
};

const tituloCelda = (fila, c, estado) => {
  if (c.fija) return estado === 'err' ? (erroresDe(fila, c)[0]?.mensaje || 'Error') : 'Campo fijo · no editable';
  const raw = valorDe(fila, c);
  if (estado === 'err') return erroresDe(fila, c)[0]?.mensaje || fila.notas[c.k] || 'Falta el dato';
  if (estado === 'sug') return fila.notas[c.k] || 'Sugerencia: confírmala';
  const monto = c.kind === 'amount' && raw !== '' ? money2(raw) : '';
  if (estado === 'man') return `Editado a mano${monto ? ` · ${monto}` : ''}`;
  return monto;
};

/*
 * P3 · Reporte (pantalla principal): resumen, barra de herramientas, tabla por
 * pestañas con el color de cada celda, y los paneles/modales que salen de ella.
 * Reporte cerrado o enviado = misma pantalla sin edición.
 */
export const BuroReporteTabla = ({ id, onBack }) => {
  const toast = useAdminToast();
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('persona');
  const [solo, setSolo] = useState(false);
  const [q, setQ] = useState('');
  const [menu, setMenu] = useState(null);       // { filaId, x, y }
  const [celda, setCelda] = useState(null);     // { filaId, k }
  const [recup, setRecup] = useState(null);     // { filaId } · resultado de un auto recuperado (celda 58)
  const [lista, setLista] = useState(null);
  const [ficha, setFicha] = useState(null);     // { clave, cola, hechos }
  const [modal, setModal] = useState(null);     // 'regenerar' | 'ajustes' | 'cerrar' | { excluir: filaId }
  const [historial, setHistorial] = useState(null); // { filtro }
  const [configPm, setConfigPm] = useState(false);  // panel de datos de Buró para cerrar
  const [ocupado, setOcupado] = useState(null); // texto del loader
  const [guardando, setGuardando] = useState(false);
  const guardandoRef = useRef(false); // evita un segundo guardado antes de que React repinte
  const [errPanel, setErrPanel] = useState(null);

  const avisar = useCallback((severity, detail) => {
    toast?.current?.show({ severity, summary: severity === 'error' ? 'Error' : 'Ok', detail });
  }, [toast]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    buroApi.getReporte(id, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setReporte(r); })
      .catch((e) => {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setError(mensajeError(e, 'No se pudo cargar el reporte.'));
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [id]);

  // Operación larga que devuelve el detalle completo del reporte.
  const correr = async (texto, accion, ok) => {
    setModal(null);
    setOcupado(texto);
    try {
      setReporte(await accion());
      if (ok) avisar('success', typeof ok === 'function' ? ok() : ok);
    } catch (e) {
      avisar('error', mensajeError(e, 'No se pudo completar la operación.'));
    } finally {
      setOcupado(null);
    }
  };

  const formato = useMemo(() => formatoDe(reporte), [reporte]);
  const pm = formato.tipo === 'pm_pfae';
  const pestana = formato.tabs[tab] ? tab : formato.inicial;
  const filas = reporte?.filas;
  const editable = reporte?.estatus === 'borrador';
  const t = reporte?.totales;

  const porId = useMemo(() => new Map((filas || []).map((f) => [f.id, f])), [filas]);
  const faltantes = useMemo(() => (reporte ? clientesFaltantes(reporte, pm) : []), [reporte, pm]);
  const soloReporteN = faltantes.filter((k) => (filas || []).some((f) => f.clienteKey === k && !f.cliente_ligado)).length;
  const manuales = useMemo(
    () => (filas || []).reduce((a, f) => a + Object.values(f.fuentes).filter((s) => s === 'manual').length, 0),
    [filas],
  );

  const columnas = tab === 'todas' ? formato.todas : formato.tabs[pestana];
  const mostrarBuro = pm && columnas.some((c) => Number(c.k) === COL_CLAVE_OBSERVACION);

  // ── Celdas pendientes: insignias por pestaña y salto a la siguiente ──
  const pendientes = useMemo(() => (filas ? celdasPendientes(filas, formato) : []), [filas, formato]);
  const porPestana = useMemo(() => {
    const n = Object.fromEntries(formato.pestanas.map(([k]) => [k, k === 'todas' ? pendientes.length : 0]));
    pendientes.forEach((p) => { if (n[p.tab] !== undefined) n[p.tab] += 1; });
    return n;
  }, [pendientes, formato]);
  const saltoRef = useRef(-1);
  const [resaltada, setResaltada] = useState(null); // { filaId, k }

  const irAPendiente = () => {
    if (pendientes.length === 0) {
      avisar('warn', 'El error está en una columna que la tabla no muestra. Regenera el reporte o revisa los avisos de la fila.');
      return;
    }
    saltoRef.current = (saltoRef.current + 1) % pendientes.length;
    const p = pendientes[saltoRef.current];
    setTab(p.tab);
    const fila = filas.find((f) => f.id === p.filaId);
    if (q && !norm(fila.cliente).includes(norm(q)) && !norm(fila.numero_cuenta).includes(norm(q))) setQ('');
    setResaltada({ filaId: p.filaId, k: p.k, n: saltoRef.current });
  };

  // Celdas con un aviso de "confirmar con Buró" (VR/FR): columna del aviso por regla.
  const celdasAviso = useMemo(() => (filas || [])
    .filter((f) => f.incluir)
    .flatMap((f) => (f.errores || []).filter(esAviso).map((e) => ({ filaId: f.id, k: AVISO_COL[e.regla] || String(e.columna), tab: 'credito' }))), [filas]);
  const avisoRef = useRef(-1);

  const irAAviso = () => {
    if (celdasAviso.length === 0) return;
    avisoRef.current = (avisoRef.current + 1) % celdasAviso.length;
    const a = celdasAviso[avisoRef.current];
    setTab(a.tab);
    const fila = filas.find((f) => f.id === a.filaId);
    if (fila && q && !norm(fila.cliente).includes(norm(q)) && !norm(fila.numero_cuenta).includes(norm(q))) setQ('');
    setResaltada({ filaId: a.filaId, k: a.k, n: avisoRef.current });
    // Abre el editor de la celda del aviso para confirmarlo de inmediato.
    setRecup(null);
    setErrPanel(null);
    setCelda({ filaId: a.filaId, k: a.k });
  };

  // Cuando ya se pintó la pestaña, lleva la celda a la vista y la resalta ~2 s.
  useEffect(() => {
    if (!resaltada) return undefined;
    const el = document.querySelector(`[data-celda="${resaltada.filaId}-${resaltada.k}"]`);
    if (el) el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    const t = setTimeout(() => setResaltada(null), 2200);
    return () => clearTimeout(t);
  }, [resaltada, tab]);
  const visibles = useMemo(() => {
    if (!filas) return [];
    const busca = norm(q);
    return filas.filter((f) => {
      if (busca && !norm(f.cliente).includes(busca) && !norm(f.numero_cuenta).includes(busca)) return false;
      if (!solo) return true;
      const e = estadoFila(f);
      return e === 'err' || e === 'sug';
    });
  }, [filas, q, solo]);

  // ── Edición de celdas ──────────────────────────────────────────────────
  const cerrarPaneles = () => { setCelda(null); setRecup(null); setLista(null); setFicha(null); setHistorial(null); setConfigPm(false); setErrPanel(null); };

  // Las filas de auto recuperado (fila.recuperacion) abren el selector de resultado
  // en la celda 58; el resto de las celdas y filas usan el editor de siempre.
  const esCeldaRecuperacion = (fila, c) => c.k === formato.resultado && !!fila.recuperacion;

  // Guarda forma/resultado/monto en el contrato. La respuesta trae las filas del
  // contrato ya rearmadas (titular y aval): se fusionan sin recargar la tabla.
  const guardarRecuperacion = async (fila, body) => {
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    setErrPanel(null);
    try {
      const resp = await buroApi.resultadoRecuperacion(id, fila.id, body);
      setReporte((prev) => fusionarFilas(prev, resp));
      cerrarPaneles();
      avisar('success', 'Resultado guardado: las celdas de la cuenta se calcularon de nuevo');
    } catch (e) {
      setErrPanel(mensajeError(e, 'No se pudo guardar el resultado de la recuperación.'));
    } finally {
      setGuardando(false);
      guardandoRef.current = false;
    }
  };

  const guardarCelda = async (fila, { columna, valor, aplicarEnSistema, motivo, confirmar, completarDomicilio, cambios }, ok) => {
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    setErrPanel(null);
    try {
      // `cambios`: C.P. de PM con su domicilio, todo en un solo PATCH.
      const resp = cambios?.length ? await buroApi.editarCeldas(id, fila.id, { cambios, aplicarEnSistema, motivo }) : await buroApi.editarCelda(id, fila.id, { columna, valor, aplicarEnSistema, motivo, confirmar });
      setReporte((prev) => fusionarFilas(prev, resp));
      // Lo que se muestra sale de la respuesta del servidor: si guardó vacío algo
      // que no lo era (formato inválido), se avisa y el panel se queda abierto.
      const devuelta = (resp.filas || []).find((f) => f.id === fila.id);
      if (!confirmar && columna !== 0 && String(valor ?? '').trim() !== '' && devuelta && String((devuelta.valores || {})[columna] ?? '') === '') {
        setErrPanel('No se pudo guardar este valor (formato inválido).');
        avisar('warn', 'No se pudo guardar este valor (formato inválido)');
        return;
      }
      cerrarPaneles();
      if (completarDomicilio) setFicha({ clave: fila.clienteKey, cola: false, hechos: 0 });
      const sale = columna === 0 && (pm ? valor === 'PF' : valor !== 'PF');
      const sistema = !!aplicarEnSistema;
      avisar('success', sale
        ? `La fila sale del reporte: va en el reporte de ${pm ? 'Personas Físicas' : 'Buró Empresas'}`
        : ok || (sistema ? 'Guardado en el sistema: aparecerá en los próximos reportes' : 'Solo en este reporte'));
    } catch (e) {
      setErrPanel(mensajeError(e, 'No se pudo guardar la celda.'));
    } finally {
      setGuardando(false);
      guardandoRef.current = false;
    }
  };

  // Confirmar / retirar un aviso de "confirmar con Buró" (VR/FR) de una fila.
  const confirmarAvisoCelda = async (filaId, regla, confirmar) => {
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    setErrPanel(null);
    try {
      const resp = await buroApi.confirmarAviso(id, filaId, { regla, confirmar });
      setReporte((prev) => fusionarFilas(prev, resp));
      cerrarPaneles();
      avisar('success', confirmar ? 'Aviso confirmado con Buró' : 'Confirmación retirada');
    } catch (e) {
      setErrPanel(mensajeError(e, 'No se pudo confirmar el aviso.'));
    } finally {
      setGuardando(false);
      guardandoRef.current = false;
    }
  };

  const guardarFicha = async (cambios, filasCliente, fichaActual, soloReporte) => {
    const cola = fichaActual.cola;
    if (!cambios.length && !cola) { cerrarPaneles(); return; }
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    setErrPanel(null);
    try {
      let actual = reporte;
      const rechazados = [];
      // Todas las columnas de la ficha viajan en UN PATCH por fila:
      //  - cliente ligado: una sola petición "también en el sistema" (el backend
      //    rearma todos los contratos del cliente);
      //  - sin cliente ligado: una por contrato incluido, solo en el reporte
      //    (quedan como editadas a mano).
      // El tipo de persona (col. 0) va aparte y al final.
      const destinos = soloReporte ? filasCliente.filter((f) => f.incluir) : [filasCliente[0]];
      const columnas = cambios.filter((c) => c.columna !== 0);
      const tipo = cambios.find((c) => c.columna === 0);
      const registrar = (resp, destino, enviados) => {
        actual = fusionarFilas(actual, resp);
        // Se revisa lo que devolvió el servidor: un valor no vacío que vuelve vacío es inválido.
        const devuelta = (resp.filas || []).find((f) => f.id === destino.id);
        enviados.forEach((c) => {
          if (c.columna !== 0 && String(c.valor ?? '').trim() !== '' && devuelta && String((devuelta.valores || {})[c.columna] ?? '') === '') {
            rechazados.push(formato.col[String(c.columna)]?.label || `columna ${c.columna}`);
          }
        });
      };
      if (columnas.length) {
        for (const destino of destinos) {
          registrar(await buroApi.editarCeldas(id, destino.id, {
            cambios: columnas, aplicarEnSistema: !soloReporte, motivo: 'Ficha del cliente',
          }), destino, columnas);
        }
      }
      if (tipo) {
        // Con cliente ligado: una petición, en el sistema. Sin cliente ligado: una
        // por contrato incluido, solo en este reporte (confirma que la fila va en el archivo).
        for (const destino of (soloReporte ? destinos : [filasCliente[0]])) {
          registrar(await buroApi.editarCelda(id, destino.id, {
            columna: 0, valor: tipo.valor, aplicarEnSistema: !soloReporte, motivo: 'Ficha del cliente',
          }), destino, []);
        }
      }
      // El estado sale de las respuestas (ya fusionadas); el GET completo solo
      // se usa como recuperación si algo falla (catch).
      setReporte(actual);
      if (rechazados.length) {
        setErrPanel(`No se pudo guardar este valor (formato inválido): ${rechazados.join(', ')}.`);
        avisar('warn', `No se pudo guardar este valor (formato inválido): ${rechazados.join(', ')}`);
        return;
      }
      const clave = filasCliente[0].clienteKey;
      if (cola) {
        // En la cola solo se avanza si el cliente quedó completo; si no, se
        // muestra lo que falta con el mensaje del backend.
        const cols = columnasDeFicha(actual, pm);
        const pendientes = [...new Set(actual.filas
          .filter((f) => f.clienteKey === clave && f.incluir)
          .flatMap((f) => f.errores.filter((e) => cols.has(Number(e.columna))).map((e) => e.mensaje)))];
        if (pendientes.length) {
          setErrPanel(`Este cliente aún tiene datos por corregir:\n${pendientes.map((m) => `• ${m}`).join('\n')}`);
          return;
        }
        const siguiente = clientesFaltantes(actual, pm).find((k) => k !== clave);
        if (siguiente) {
          setFicha({ clave: siguiente, cola: true, hechos: fichaActual.hechos + 1 });
          avisar('success', soloReporte ? 'Guardado solo en este reporte · siguiente cliente' : 'Guardado en el sistema · siguiente cliente');
        } else {
          cerrarPaneles();
          avisar('success', 'Listo: no quedan clientes con datos faltantes');
        }
      } else {
        cerrarPaneles();
        avisar('success', soloReporte ? 'Solo en este reporte' : 'Guardado en el sistema: aparecerá en los próximos reportes');
      }
    } catch (e) {
      // Puede haber quedado guardada una parte: se vuelve a pedir el reporte.
      buroApi.getReporte(id).then(setReporte).catch(() => {});
      setErrPanel(mensajeError(e, 'No se pudieron guardar los datos del cliente.'));
    } finally {
      setGuardando(false);
      guardandoRef.current = false;
    }
  };

  const guardarLista = async (cambios, aplicarEnSistema) => {
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardando(true);
    setErrPanel(null);
    try {
      const resp = await buroApi.editarCeldas(id, lista.filaId, { cambios, aplicarEnSistema, motivo: 'Lista del reporte' });
      setReporte((prev) => fusionarFilas(prev, resp));
      cerrarPaneles();
      avisar('success', 'Lista guardada');
    } catch (e) {
      setErrPanel(mensajeError(e, 'No se pudo guardar la lista.'));
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const cambiarInclusion = async (fila, incluir, motivo) => {
    setModal(null);
    try {
      const resp = await buroApi.incluirFila(id, fila.id, { incluir, motivo });
      setReporte((prev) => fusionarFilas(prev, resp));
      avisar('success', incluir ? `${fila.numero_cuenta} incluido de nuevo` : `${fila.numero_cuenta} excluido del reporte`);
    } catch (e) {
      avisar('error', mensajeError(e, 'No se pudo cambiar la fila.'));
    }
  };

  const guardarArchivo = (blob, nombre) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ZIP protegido: descarga y devuelve la contraseña para mostrarla una sola vez
  // (no se guarda en el estado). Los errores los pinta el modal.
  const descargarProtegido = async (password) => {
    const { blob, nombre, password: clave } = await buroApi.descargarArchivoProtegido(id, password);
    guardarArchivo(blob, nombre);
    return clave;
  };

  // Botones de descarga de un reporte cerrado o enviado, con el aviso de registro.
  const botonesDescarga = (grande) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className={`bu-btn ${grande ? 'bu-btn-primary bu-btn-lg' : ''}`} onClick={descargar}>
          <i className="pi pi-download" style={grande ? undefined : { fontSize: 13 }} />Descargar {formato.archivo}
        </button>
        <button type="button" className={`bu-btn ${grande ? 'bu-btn-lg' : ''}`} onClick={() => setModal('zip')}>
          <i className="pi pi-lock" style={grande ? undefined : { fontSize: 13 }} />Descargar {formato.archivo} protegido
        </button>
        {pm && (
          <button type="button" className={`bu-btn ${grande ? 'bu-btn-lg' : ''}`} title="Vista legible en Excel para revisar. No es el archivo que se envía a Buró." onClick={descargarRevision}>
            <i className="pi pi-file-excel" style={grande ? undefined : { fontSize: 13 }} />Descargar CSV (revisión)
          </button>
        )}
      </div>
      <span className="bu-sub" style={{ fontSize: 11 }}>La descarga queda registrada (quién y cuándo).{pm ? ' El CSV de revisión es solo para leerlo en Excel; a Buró se envía el TXT.' : ''}</span>
    </div>
  );

  const descargar = async () => {
    try {
      const { blob, nombre } = await buroApi.descargarArchivo(id, `buro_${formato.tipo}_${String(reporte.periodo).slice(0, 7).replace('-', '')}.${formato.extension}`);
      guardarArchivo(blob, nombre);
      avisar('success', `${formato.archivo} descargado`);
    } catch (e) {
      avisar('error', mensajeError(e, `No se pudo descargar el ${formato.archivo}.`));
    }
  };

  const descargarRevision = async () => {
    try {
      const { blob, nombre } = await buroApi.descargarCsvRevision(id, `buro_pm_pfae_${String(reporte.periodo).slice(0, 7).replace('-', '')}_revision.csv`);
      guardarArchivo(blob, nombre);
      avisar('success', 'CSV de revisión descargado');
    } catch (e) {
      avisar('error', mensajeError(e, 'No se pudo descargar el CSV de revisión.'));
    }
  };

  const abrirMenu = (ev, fila) => {
    ev.stopPropagation();
    const b = ev.currentTarget.getBoundingClientRect();
    // El menú mide ~130px (3 botones de 36px + relleno). Si no cabe debajo (últimas filas), se abre hacia arriba.
    const ALTO_MENU = 130;
    const cabeAbajo = b.bottom + 4 + ALTO_MENU <= window.innerHeight - 8;
    const y = cabeAbajo ? b.bottom + 4 : Math.max(8, b.top - ALTO_MENU - 4);
    setMenu({ filaId: fila.id, x: Math.max(8, b.right - 230), y });
  };

  const abrirFicha = (fila, cola = false) => { setMenu(null); setErrPanel(null); setFicha({ clave: fila.clienteKey, cola, hechos: 0 }); };

  if (loading && !reporte) return <div className="buro-dash"><div className="bu-msg">Cargando reporte…</div></div>;
  if (error || !reporte) {
    return (
      <div className="buro-dash">
        <button type="button" className="bu-btn bu-btn-ghost" onClick={onBack}><i className="pi pi-angle-left" />Volver</button>
        <div className="bu-msg err">{error || 'No se encontró el reporte.'}</div>
      </div>
    );
  }

  const hayErrores = t.con_error > 0;
  // Pendientes externos (Buró, Contabilidad): permiten seguir preparando el borrador, no cerrarlo.
  const pendientesCierre = editable ? (reporte.pendientes_cierre || []) : [];
  const filaCelda = celda ? porId.get(celda.filaId) : null;
  const filaRecup = recup ? porId.get(recup.filaId) : null;
  const filasFicha = ficha ? filas.filter((f) => f.clienteKey === ficha.clave) : [];
  const colaFicha = ficha?.cola ? {
    pos: ficha.hechos + 1,
    total: ficha.hechos + Math.max(faltantes.length, 1),
    siguiente: filas.find((f) => faltantes.includes(f.clienteKey) && f.clienteKey !== ficha.clave)?.cliente,
  } : null;
  const filaMenu = menu ? porId.get(menu.filaId) : null;
  const filaExcluir = modal?.excluir ? porId.get(modal.excluir) : null;

  return (
    <div className="buro-dash">
      <div className="bu-rep">
        <div className="bu-rep-head">
          <button type="button" className="bu-btn bu-btn-ghost" onClick={onBack}><i className="pi pi-angle-left" />Volver</button>
          <div className="bu-title">Reporte {formato.nombre} · {mesLargo(reporte.periodo)}</div>
          <ChipEstatus estatus={reporte.estatus} />
          <span className="bu-sub" style={{ fontSize: 13 }}>Fecha de reporte {fechaCorte(reporte.periodo)}</span>
        </div>

        <div className="bu-kpis">
          <div className="bu-kpi"><div className="k">Contratos</div><div className="v">{t.contratos}</div></div>
          <div className="bu-kpi">
            <div className="k">Listos</div>
            <div className="v">{t.listos}<i className="pi pi-check-circle" style={{ fontSize: 16, color: '#23B56E' }} /></div>
          </div>
          <button type="button" className={`bu-kpi ${solo ? 'on' : ''}`} title="Ver solo pendientes" onClick={() => setSolo((s) => !s)}>
            <div className="k">Pendientes</div>
            <div className="v" style={{ gap: 14 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#B91C1C' }}>{t.con_error}<i className="pi pi-exclamation-triangle" style={{ fontSize: 14 }} /></span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#8A6D2A' }}>{t.con_sugerencia}<i className="pi pi-info-circle" style={{ fontSize: 14 }} /></span>
            </div>
          </button>
          <div className="bu-kpi"><div className="k">Saldo actual</div><div className="v">{money0(t.saldo_actual)}</div></div>
          <div className="bu-kpi"><div className="k">Saldo vencido</div><div className="v">{money0(t.saldo_vencido)}</div></div>
        </div>

        {pendientesCierre.length > 0 && (
          <div className="bu-note bu-note-warn" role="status">
            <i className="pi pi-lock" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <span style={{ fontWeight: 600 }}>Aún no se puede cerrar el mes. Puedes seguir preparando el borrador.</span>
              {pendientesCierre.map((p) => <span key={p}>· {p}</span>)}
              <button type="button" className="bu-btn" style={{ alignSelf: 'flex-start', marginTop: 6 }} onClick={() => setConfigPm(true)}>
                <i className="pi pi-cog" /> Configurar datos de Buró
              </button>
            </div>
          </div>
        )}

        {reporte.estatus === 'cerrado' && (
          <div className="bu-envio">
            {botonesDescarga(true)}
            <button
              type="button"
              className="bu-btn"
              style={{ height: 38 }}
              onClick={() => correr('Marcando como enviado…', () => buroApi.marcarEnviado(id), 'Marcado como enviado')}
            >
              <i className="pi pi-send" style={{ fontSize: 13 }} />Marcar como enviado
            </button>
          </div>
        )}

        <div className="bu-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="bu-tools">
            <div className="bu-row">
              <div className="bu-seg">
                {formato.pestanas.map(([k, l]) => <button key={k} type="button" className={(tab === 'todas' ? tab : pestana) === k ? 'on' : ''} onClick={() => setTab(k)}>{l}{porPestana[k] > 0 && <span className="bu-badge" title="Celdas con error o faltantes">{porPestana[k]}</span>}</button>)}
              </div>
              <button type="button" className={`bu-switch ${solo ? 'on' : ''}`} onClick={() => setSolo((s) => !s)}>
                <span className="trk" />Solo pendientes
              </button>
              <div className="bu-search bu-ml">
                <i className="pi pi-search" />
                <input className="bu-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente o contrato" />
              </div>
            </div>

            {editable && (
              <div className="bu-row tight">
                {faltantes.length > 0 && (
                  <button
                    type="button"
                    className="bu-btn bu-btn-falta"
                    onClick={() => abrirFicha(filas.find((f) => f.clienteKey === faltantes[0]), true)}
                  >
                    <i className="pi pi-id-card" style={{ fontSize: 13 }} />{pm ? 'Empresas' : 'Clientes'} con datos faltantes ({faltantes.length - soloReporteN}{soloReporteN > 0 ? ` + ${soloReporteN} solo en este reporte` : ''})
                  </button>
                )}
                <div className="bu-ml" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {formato.ajustes && <button type="button" className="bu-btn" onClick={() => setModal('ajustes')}><i className="pi pi-cog" style={{ fontSize: 13 }} />Ajustes</button>}
                  <button type="button" className="bu-btn" onClick={() => setModal('regenerar')}><i className="pi pi-refresh" style={{ fontSize: 13 }} />Regenerar</button>
                  {reporte.cambios && (
                    <button type="button" className="bu-btn" onClick={() => setHistorial({ filtro: '' })}><i className="pi pi-history" style={{ fontSize: 13 }} />Historial</button>
                  )}
                  {hayErrores ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <button
                        type="button"
                        className="bu-cierre-hint"
                        title="Ir a la siguiente celda pendiente"
                        onClick={irAPendiente}
                      >
                        Faltan {t.con_error} filas por corregir{pendientes.length > 0 ? ` · ir a la celda ${((saltoRef.current + 1) % pendientes.length) + 1} de ${pendientes.length}` : ''}
                      </button>
                      <button type="button" className="bu-btn bu-btn-primary" disabled title={`Faltan ${t.con_error} filas por corregir`}>Cerrar mes<i className="pi pi-angle-right" /></button>
                    </span>
                  ) : (() => {
                    const avisos = t.con_aviso || 0;
                    const bloqueado = pendientesCierre.length > 0 || avisos > 0;
                    const motivo = pendientesCierre.length > 0
                      ? 'Hay pendientes por confirmar: ver el aviso de arriba'
                      : (avisos > 0 ? `Hay ${avisos} ${avisos === 1 ? 'fila' : 'filas'} con un aviso pendiente de confirmar con Buró (en amarillo).` : undefined);
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        {avisos > 0 && pendientesCierre.length === 0 && (
                          <button type="button" className="bu-cierre-hint" title="Ir a la celda del aviso para confirmarlo" onClick={irAAviso}>
                            {motivo}{celdasAviso.length > 0 ? ` · ir a la celda ${((avisoRef.current + 1) % celdasAviso.length) + 1} de ${celdasAviso.length}` : ''}
                          </button>
                        )}
                        <button type="button" className="bu-btn bu-btn-primary" disabled={bloqueado} title={motivo} onClick={() => setModal('cerrar')}>Cerrar mes<i className="pi pi-angle-right" /></button>
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}

            {reporte.estatus === 'enviado' && (
              <div className="bu-row tight">
                <span className="bu-sub" style={{ fontSize: 13 }}>Reporte enviado · solo lectura</span>
                <div className="bu-ml">{botonesDescarga(false)}</div>
                {reporte.cambios && (
                  <button type="button" className="bu-btn" onClick={() => setHistorial({ filtro: '' })}><i className="pi pi-history" style={{ fontSize: 13 }} />Historial</button>
                )}
              </div>
            )}
          </div>

          <div className="bu-scroll">
            {/* Columna extra solo en PM: cómo se verá la cuenta en el Buró según la clave CR19. */}
            <table className="bu-tbl">
              <thead>
                <tr>
                  <th className="s s0" />
                  <th className="s s1">Contrato</th>
                  <th className="s s2">Cliente</th>
                  {columnas.map((c) => {
                    const ayuda = AYUDA[c.k === 'tipo_persona' ? 'tipo_persona' : Number(c.k)] || '';
                    return (
                      <th
                        key={c.k}
                        title={ayuda}
                        style={{ minWidth: c.w, textAlign: esNumerica(c) ? 'right' : 'left', cursor: ayuda ? 'help' : 'default' }}
                      >
                        {c.label} <span className="n">· {c.num}</span>
                        {ayuda && <i className="pi pi-question-circle" style={{ fontSize: 11, color: '#94A3B8', marginLeft: 4 }} />}
                      </th>
                    );
                  })}
                  {mostrarBuro && <th style={{ minWidth: 360 }}>En su Buró <span className="n">· CR19</span></th>}
                </tr>
              </thead>
              <tbody>
                {visibles.map((f) => {
                  const ef = estadoFila(f);
                  const [ico, icoColor, icoTitle] = FILA[ef];
                  const excl = !f.incluir;
                  return (
                    <tr key={f.id} className={excl ? 'excl' : ''}>
                      <td className="s s0" title={excl ? textoExclusion(f) : icoTitle} style={{ color: icoColor }}>
                        <i className={`pi ${ico}`} style={{ fontSize: 13 }} />
                      </td>
                      <td className="s s1"><span className="nom" style={excl ? undefined : { color: '#0C2036' }}>{f.numero_cuenta}</span></td>
                      <td className="s s2">
                        <div className="bu-cli">
                          <span
                            className="nom"
                            title="Abrir ficha del cliente"
                            onClick={() => { if (editable && !excl && !f.aval) abrirFicha(f); }}
                          >{f.cliente}</span>
                          {f.aval && <span className="tag" style={{ background: '#E8F7FD', color: '#0E5E80' }}>Aval</span>}
                          {excl && <span className="tag">Excluido</span>}
                          <button type="button" className="bu-icon-btn" title="Más acciones" onClick={(ev) => abrirMenu(ev, f)}>
                            <i className="pi pi-ellipsis-h" style={{ fontSize: 13 }} />
                          </button>
                        </div>
                      </td>
                      {columnas.map((c) => {
                        const est = estadoCelda(f, c);
                        const [bg, fg, icon] = COLOR[est];
                        const texto = textoCelda(f, c);
                        const puede = editable && !c.fija && f.incluir && !(pm && (c.lista === 'AC' || c.segmento === 'AC') && f.tipo_persona === 'PFAE');
                        const recupCelda = esCeldaRecuperacion(f, c);
                        const dist = recupCelda ? distintivoRecuperacion(f.recuperacion) : null;
                        return (
                          <td
                            key={c.k}
                            data-celda={`${f.id}-${c.k}`}
                            className={`c ${resaltada && resaltada.filaId === f.id && resaltada.k === c.k ? 'resaltada' : ''} ${puede ? 'edit' : ''} ${esNumerica(c) ? 'num' : ''} ${est === 'err' && !texto ? 'vacio' : ''} ${excl ? 'exc' : ''}`}
                            title={recupCelda ? `${dist.titulo}${puede ? ' · clic para cambiar' : ''}` : tituloCelda(f, c, est)}
                            style={{ minWidth: c.w, background: bg, color: fg }}
                            onClick={puede ? () => { setErrPanel(null); if (pm && (c.lista || ['AC', 'AV'].includes(c.segmento))) { setLista({ filaId: f.id, segmento: c.lista || c.segmento }); } else if (recupCelda) { setCelda(null); setRecup({ filaId: f.id }); } else { setRecup(null); setCelda({ filaId: f.id, k: c.k }); } } : undefined}
                          >
                            {icon && <i className={`pi ${icon}`} />}{texto || (est === 'err' ? 'vacío' : '')}
                            {dist && <span className={`bu-recup ${dist.clase}`}><i className="pi pi-car" />{dist.texto}</span>}
                          </td>
                        );
                      })}
                      {mostrarBuro && (() => {
                        const ex = f.incluir ? explicacionBuro(f.valores) : null;
                        return (
                          <td className="c" title={ex ? ex.texto : ''} style={{ minWidth: 360, whiteSpace: 'normal', fontSize: 12, color: '#334155' }}>
                            {ex && <><b>{ex.clave}</b> · {ex.texto}</>}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {visibles.length === 0 && <div className="bu-msg">No hay filas con este filtro.</div>}
          </div>

          <div className="bu-leyenda">
            <b>Leyenda</b>
            <span className="it"><span className="sw" style={{ background: '#FFFFFF', borderColor: '#CBD5E1' }} />Sistema</span>
            <span className="it"><span className="sw" style={{ background: '#FFF6E5', borderColor: '#E8D4A8' }} /><i className="pi pi-info-circle" style={{ fontSize: 11, color: '#8A6D2A' }} />Sugerencia</span>
            <span className="it"><span className="sw" style={{ background: '#FDECEC', borderColor: '#F3C4C4' }} /><i className="pi pi-exclamation-triangle" style={{ fontSize: 11, color: '#B91C1C' }} />Falta / error</span>
            <span className="it"><span className="sw" style={{ background: '#E8F7FD', borderColor: '#B5E3F5' }} /><i className="pi pi-pencil" style={{ fontSize: 11, color: '#0E5E80' }} />Manual</span>
            <span className="bu-ml">{visibles.length} de {filas.length} filas</span>
          </div>
        </div>
      </div>

      {filaMenu && (
        <BuroPortal>
          <div className="bu-menu-back" onClick={() => setMenu(null)} />
          <div className="bu-menu" style={{ left: menu.x, top: menu.y }}>
            {/* En otra pestaña: así no se pierde la posición ni lo capturado en el reporte. */}
            <button type="button" onClick={() => { setMenu(null); window.open(`/admin/cartera?credito=${filaMenu.credito_id}`, '_blank', 'noopener'); }}>
              <i className="pi pi-external-link" />Ver contrato en Cartera
            </button>
            {editable && filaMenu.incluir && !filaMenu.aval && (
              <button type="button" onClick={() => abrirFicha(filaMenu)}><i className="pi pi-id-card" />{pm ? 'Datos de la empresa' : 'Datos del cliente'}</button>
            )}
            {editable && (filaMenu.incluir ? (
              <button type="button" className="danger" onClick={() => { setMenu(null); setModal({ excluir: filaMenu.id }); }}>
                <i className="pi pi-ban" />Excluir del reporte
              </button>
            ) : (
              <button type="button" onClick={() => { setMenu(null); cambiarInclusion(filaMenu, true, ''); }}>
                <i className="pi pi-replay" />Incluir de nuevo
              </button>
            ))}
            {reporte.cambios && (
              <button type="button" onClick={() => { setMenu(null); setHistorial({ filtro: filaMenu.numero_cuenta }); }}>
                <i className="pi pi-history" />Ver cambios de esta fila
              </button>
            )}
          </div>
        </BuroPortal>
      )}

      {filaCelda && (
        <BuroCeldaEditor
          key={`${filaCelda.id}-${celda.k}`}
          fila={filaCelda}
          col={formato.col[celda.k] || formato.todas.find((c) => c.k === celda.k)}
          formato={formato}
          nContratos={filas.filter((f) => f.clienteKey === filaCelda.clienteKey).length}
          filas={filas}
          corregibles={reporte.columnas_corregibles}
          guardando={guardando}
          error={errPanel}
          onClose={cerrarPaneles}
          onGuardar={(d) => guardarCelda(filaCelda, d)}
          onConfirmar={(columna) => guardarCelda(filaCelda, { columna, confirmar: true }, 'Sugerencia confirmada')}
          onConfirmarAviso={(filaId, regla, confirmar) => confirmarAvisoCelda(filaId, regla, confirmar)}
          onRestaurar={(columna, valor) => guardarCelda(filaCelda, { columna, valor, aplicarEnSistema: false, motivo: 'Restaurado al valor del sistema' }, 'Valor del sistema restaurado')}
        />
      )}

      {filaRecup?.recuperacion && (
        <BuroRecuperacionEditor
          key={filaRecup.id}
          fila={filaRecup}
          codigoResultado={formato.resultado}
          guardando={guardando}
          error={errPanel}
          onClose={cerrarPaneles}
          onGuardar={(body) => guardarRecuperacion(filaRecup, body)}
          onEditarCelda={() => { setRecup(null); setErrPanel(null); setCelda({ filaId: filaRecup.id, k: formato.resultado }); }}
        />
      )}

      {!pm && ficha && filasFicha.length > 0 && (
        <BuroFichaCliente
          key={ficha.clave}
          filas={filasFicha}
          todas={filas}
          cola={colaFicha}
          guardando={guardando}
          error={errPanel}
          onClose={cerrarPaneles}
          onGuardar={(cambios, soloReporte) => guardarFicha(cambios, filasFicha, ficha, soloReporte)}
        />
      )}

      {pm && ficha && filasFicha.length > 0 && <BuroFichaEmpresa key={ficha.clave} filas={filasFicha} columnas={formato.tabs.empresa} cola={colaFicha} guardando={guardando} error={errPanel} onClose={cerrarPaneles} onGuardar={(cambios, soloReporte) => guardarFicha(cambios, filasFicha, ficha, soloReporte)} />}
      {lista && porId.has(lista.filaId) && <BuroListaEditor key={`${lista.filaId}-${lista.segmento}`} fila={porId.get(lista.filaId)} segmento={lista.segmento} columnas={formato.listas[lista.segmento]} guardando={guardando} error={errPanel} onClose={cerrarPaneles} onGuardar={guardarLista} />}

      {historial && reporte.cambios && (
        <BuroHistorial cambios={reporte.cambios} filas={filas} inicial={historial.filtro} onClose={cerrarPaneles} />
      )}

      {configPm && (
        <BuroConfigPm
          onClose={cerrarPaneles}
          onGuardado={() => buroApi.getReporte(id).then(setReporte).catch(() => {})}
        />
      )}

      {formato.ajustes && modal === 'ajustes' && (
        <AjustesModal
          ajustes={reporte.ajustes}
          onClose={() => setModal(null)}
          onGuardar={(a) => correr('Regenerando con los nuevos ajustes…', () => buroApi.guardarAjustes(id, a), 'Ajustes guardados · reporte regenerado')}
        />
      )}
      {modal === 'regenerar' && (
        <RegenerarModal
          tipo={formato.tipo}
          manuales={manuales}
          onClose={() => setModal(null)}
          onConfirm={() => correr(`Regenerando ${filas.length} contratos…`, () => buroApi.regenerar(id), () => `Reporte regenerado · se conservaron ${manuales} ediciones manuales`)}
        />
      )}
      {modal === 'zip' && <CsvProtegidoModal tipo={formato.tipo} onClose={() => setModal(null)} onGenerar={descargarProtegido} />}
      {modal === 'cerrar' && (
        <CerrarModal
          reporte={reporte}
          onClose={() => setModal(null)}
          onConfirm={() => correr('Cerrando el mes…', () => buroApi.cerrar(id), () => `${mesLargo(reporte.periodo).split(' ')[0]} cerrado`)}
        />
      )}
      {filaExcluir && (
        <ExcluirModal tipo={formato.tipo} fila={filaExcluir} onClose={() => setModal(null)} onConfirm={(motivo) => cambiarInclusion(filaExcluir, false, motivo)} />
      )}
      {ocupado && <Cargando texto={ocupado} />}
    </div>
  );
};

export default BuroReporteTabla;
